import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'
import { Button } from '#/components/ui/button'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { Input } from '#/components/ui/input'
import { AlertBox } from '#/components/ui/alert-box'
import { mutationError } from '#/lib/mutation-error'
import { organizationAPI } from '#/api/organization'
import { personalKeycardAPI } from '#/api/personalKeycard'


interface FormInputs {
  hardwareId: string
  friendlyId: string
  email: string
}

export default function NewPersonalKeycardModal() {
  const { t } = useTranslation([ 'personal_keycards', 'general' ])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const params = useParams() as { organization: string }

  const close = () => navigate('..')

  // Fetch the organization for longId
  const { data: organization } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization ],
    queryFn: () => organizationAPI.get(params.organization)
  })

  const form = useForm<FormInputs>({
    defaultValues: {
      hardwareId: '',
      friendlyId: '',
      email: ''
    }
  })

  // Mutation to create personal keycard
  const mutation = useMutation({
    mutationFn: (data: FormInputs) => personalKeycardAPI.create({
      organizationId: organization.id,
      ...data
    }),
    onSuccess: created => {
      queryClient.setQueryData(
        [ 'organizations', params.organization, 'personalKeycards', created.id ],
        created
      )
      navigate('..')
    },
    onError: () => {
      // handled via AlertBox
    }
  })

  return (
    <Dialog open modal onOpenChange={close}>
      <DialogContent className='max-h-[80%] overflow-y-scroll'>
        <DialogHeader>
          <DialogTitle>{t('new_personal_keycard.title')}</DialogTitle>
          <DialogDescription>{t('new_personal_keycard.description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(values => mutation.mutate(values))}
            className="flex flex-col gap-4"
            autoComplete="off"
          >

            {mutation.error && (
              <AlertBox
                error
                title={mutationError(mutation.error, {
                  default: t('new_personal_keycard.errors.default'),
                  forbidden: t('new_personal_keycard.errors.forbidden')
                })}
              />
            )}

            {/* Hardware ID */}
            <FormField
              name="hardwareId"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.hardwareId')}</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Friendly ID */}
            <FormField
              name="friendlyId"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.friendlyId')}</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.email')}</FormLabel>
                  <FormControl>
                    <Input
                      type='email'
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={close}>
                {t('general:cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {t('new_personal_keycard.submit')}
              </Button>
            </DialogFooter>

          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
